import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// All auth flows through httpOnly cookies (set by /auth/login).
// No tokens are stored in localStorage to avoid XSS exposure.
const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Phase 119 — Session Fix: a 401 mid-session (cookie expired/invalidated)
// used to just surface the backend's raw "Not authenticated" detail as a
// toast from whichever call site happened to fail, leaving the user stuck
// on a dead page. Now it's caught globally, shown as one clear message,
// and sent straight back to /login. Excludes the login/auth-check calls
// themselves — those 401s are expected (wrong password / not-yet-logged-in)
// and are already handled by their own callers.
let sessionExpiredHandled = false;
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    const isAuthCheck = url.includes("/auth/login") || url.includes("/auth/me") || url.includes("/auth/register");
    const onLoginPage = typeof window !== "undefined" && window.location.pathname === "/login";
    if (status === 401 && !isAuthCheck && !onLoginPage && !sessionExpiredHandled) {
      sessionExpiredHandled = true;
      toast.error("Your session expired, please sign in again");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// Oct 2026 — Shared dropdown lists: several components on one page each
// fetch the same lists (categories, projects, accounts, vendors, holidays).
// Identical GETs for these lists now share one request for up to 60s. Any
// save (POST/PUT/PATCH/DELETE) clears the cache so new entries show at once.
const SHARED_LIST_URLS = new Set([
  "/finance/categories", "/daily-tasks/projects", "/finance/sources",
  "/finance/vendor-options", "/leaves/holidays",
]);
const SHARED_LIST_TTL_MS = 60000;
const sharedListCache = new Map();
const cloneResponse = (r) => ({ ...r, data: r?.data == null ? r?.data : (typeof structuredClone === "function" ? structuredClone(r.data) : JSON.parse(JSON.stringify(r.data))) });
const rawGet = api.get.bind(api);
api.get = (url, config) => {
  if (!SHARED_LIST_URLS.has(url)) return rawGet(url, config);
  const key = `${url}|${JSON.stringify(config?.params || {})}`;
  const hit = sharedListCache.get(key);
  if (hit && Date.now() - hit.t < SHARED_LIST_TTL_MS) return hit.p.then(cloneResponse);
  const p = rawGet(url, config);
  sharedListCache.set(key, { p, t: Date.now() });
  p.catch(() => sharedListCache.delete(key));
  return p.then(cloneResponse);
};
const isWrite = (cfg) => (cfg?.method || "get").toLowerCase() !== "get";
api.interceptors.request.use((cfg) => { if (isWrite(cfg)) sharedListCache.clear(); return cfg; });
api.interceptors.response.use(
  (r) => { if (isWrite(r?.config)) sharedListCache.clear(); return r; },
  (e) => { if (isWrite(e?.config)) sharedListCache.clear(); return Promise.reject(e); },
);

export default api;

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function getWsUrl() {
  // Same-origin WebSocket — browser sends httpOnly access_token cookie automatically.
  const base = BACKEND_URL.replace(/^http/, "ws");
  return `${base}/api/ws/chat`;
}
